import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  CircularProgress,
  Paper,
  Avatar,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import axios from "axios";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import PastVisits from "../doctor-console/PastVisits/PastVisits";
import CaseSheetModal from "../appointments/CaseSheetModal";
import { displayPatientNameFromParts } from "../../utils/displayPatientName";

const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;

// Minimum typed characters before patient search API is called
const PATIENT_SEARCH_MIN_LENGTH = 2;
// Delay between keystrokes before triggering patient search
const PATIENT_SEARCH_DEBOUNCE_MS = 500;

export default function PatientLocalData() {
  const history = useHistory();
  const [authToken] = useState(
    sessionStorage.getItem("accessToken") || ""
  );
  const headers = { Authorization: `Bearer ${authToken}` };

  const [isAbdmEnabled] = useState(
    JSON.parse(sessionStorage.getItem("isAbdmEnabled") || "false")
  );

  const [patientList, setPatientList] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const [careContextList, setCareContextList] = useState([]);
  const [s3BucketFhir, setS3BucketFhir] = useState(null);
  const [isCaseSheetOpen, setIsCaseSheetOpen] = useState(false);
  const [selectedContextForRetrieveFhir, setSelectedContextForRetrieveFhir] =
    useState([]);

  // Debounce timer and abort controller for in-flight patient search requests
  const patientSearchDebounceRef = useRef(null);
  const patientSearchAbortRef = useRef(null);

  // Clear debounce timer and abort any pending search on unmount
  useEffect(() => {
    return () => {
      if (patientSearchDebounceRef.current) {
        clearTimeout(patientSearchDebounceRef.current);
      }
      if (patientSearchAbortRef.current) {
        patientSearchAbortRef.current.abort();
      }
    };
  }, []);

  /**
   * Fetch patients matching the search query from the server.
   * Called only after the user types at least PATIENT_SEARCH_MIN_LENGTH characters.
   */
  const fetchPatientList = (searchQuery) => {
    if (patientSearchAbortRef.current) {
      patientSearchAbortRef.current.abort();
    }

    const abortController = new AbortController();
    patientSearchAbortRef.current = abortController;

    setLoadingPatients(true);
    axios
      .get(`${REACT_APP_BACKEND_ROUTE_URL}get_patient_list_with_aabha_info/`, {
        headers,
        params: { search: searchQuery },
        signal: abortController.signal,
      })
      .then((res) => {
        if (abortController.signal.aborted) {
          return;
        }
        setPatientList(res.data?.data || []);
      })
      .catch((err) => {
        if (axios.isCancel(err) || err.name === "CanceledError" || err.code === "ERR_CANCELED") {
          return;
        }
        console.error("Failed to fetch patients:", err);
        toast.error("Failed to load patient list");
        setPatientList([]);
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setLoadingPatients(false);
        }
      });
  };

  /**
   * Handle Autocomplete input changes: debounce API calls and enforce minimum search length.
   * Skips fetch on programmatic input updates (e.g. when a patient is selected).
   */
  const handlePatientSearchInputChange = (event, newInputValue, reason) => {
    if (reason === "reset") {
      return;
    }

    if (patientSearchDebounceRef.current) {
      clearTimeout(patientSearchDebounceRef.current);
      patientSearchDebounceRef.current = null;
    }

    const trimmedSearch = (newInputValue || "").trim();

    if (trimmedSearch.length < PATIENT_SEARCH_MIN_LENGTH) {
      if (patientSearchAbortRef.current) {
        patientSearchAbortRef.current.abort();
        patientSearchAbortRef.current = null;
      }
      setLoadingPatients(false);
      setPatientList([]);
      return;
    }

    patientSearchDebounceRef.current = setTimeout(() => {
      fetchPatientList(trimmedSearch);
    }, PATIENT_SEARCH_DEBOUNCE_MS);
  };

  const fetchCareContexts = (patient) => {
    const postData = {
      filter_details: {},
    };

    if (isAbdmEnabled && patient.abha_id) {
      postData.filter_type = "patient_abha";
      postData.filter_details.patient_abha = patient.abha_id;
    } else {
      postData.filter_type = "patient_id";
      postData.filter_details.patient_id = patient.patient_id;
    }

    axios
      .post(
        `${REACT_APP_BACKEND_ROUTE_URL}fetch_care_contexts_by_filter/`,
        JSON.stringify(postData),
        { headers }
      )
      .then((res) => {
        if (res.data.status !== "Failed") {
          setCareContextList(res.data.care_contenxts || []);
        }
      })
      .catch((err) => console.error("Care context fetch error:", err));
  };

  const handlePatientSelect = (event, patient) => {
    if (!patient) {
      setSelectedPatient(null);
      setCareContextList([]);
      setS3BucketFhir(null);
      return;
    }
    setSelectedPatient(patient);
    setS3BucketFhir(null);
    setSelectedContextForRetrieveFhir([]);
    fetchCareContexts(patient);
  };

  const handleFhirArray = (fhirArray) => {
    setS3BucketFhir(fhirArray);
  };

  const handleOpenCaseSheet = () => {
    if (!selectedPatient?.patient_id) {
      return;
    }
    setIsCaseSheetOpen(true);
  };

  const handleCreateAppointment = () => {
    if (!selectedPatient?.patient_id) {
      return;
    }

    const prefillPatient = {
      patient_id: selectedPatient.patient_id,
      name: selectedPatient.name || "",
      email: selectedPatient.email || "",
      phone_no: selectedPatient.phone_no || "",
      abha_id: selectedPatient.abha_id || "",
      abha_no: selectedPatient.abha_no || "",
      gender: selectedPatient.gender || "",
      dob: selectedPatient.dob || "",
      photo: selectedPatient.photo || "",
    };

    history.push({
      pathname: "/doctor/appointments",
      state: {
        preselectedAppointmentPatient: prefillPatient,
      },
    });
  };

  const handleViewInvoice = () => {
    if (!selectedPatient?.patient_id) {
      return;
    }

    const prefillPatient = {
      patient_id: selectedPatient.patient_id,
      pat_id: selectedPatient.patient_id,
      name: selectedPatient.name || "",
      patient_name: selectedPatient.name || "",
      email: selectedPatient.email || "",
      patient_email: selectedPatient.email || "",
      phone_no: selectedPatient.phone_no || "",
      abha_id: selectedPatient.abha_id || "",
      abha_no: selectedPatient.abha_no || "",
      gender: selectedPatient.gender || "",
      dob: selectedPatient.dob || "",
      photo: selectedPatient.photo || "",
    };

    history.push({
      pathname: "/doctor/invoice",
      state: {
        preselectedInvoicePatient: prefillPatient,
      },
    });
  };

  const getPatientLabel = (patient) => {
    const name = displayPatientNameFromParts(patient, "Unknown");
    const id = patient.patient_id || "";
    const phone = patient.phone_no || "";
    return `${name} | ${id}${phone ? ` | ${phone}` : ""}`;
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: 1100, margin: "auto" }}>
      {/* Search Section */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: "16px",
          background: "linear-gradient(135deg, #f5f7fa 0%, #e4ecf7 100%)",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            color: "#125386",
            fontWeight: 700,
            mb: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <SearchIcon sx={{ fontSize: 28 }} />
          Patient Historical Records
        </Typography>
        <Autocomplete
          options={patientList}
          loading={loadingPatients}
          getOptionLabel={getPatientLabel}
          onInputChange={handlePatientSearchInputChange}
          onChange={handlePatientSelect}
          filterOptions={(options, { inputValue }) => {
            const search = inputValue.toLowerCase();
            return options.filter((opt) => {
              const name = displayPatientNameFromParts(opt, "").toLowerCase();
              const id = (opt.patient_id || "").toLowerCase();
              const phone = (opt.phone_no || "").toLowerCase();
              return (
                name.includes(search) ||
                id.includes(search) ||
                phone.includes(search)
              );
            });
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.patient_id}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar sx={{ bgcolor: "#125386", width: 36, height: 36 }}>
                  <PersonIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                    {displayPatientNameFromParts(option, "Unknown")}
                  </Typography>
                  <Typography sx={{ fontSize: "0.8rem", color: "#666" }}>
                    ID: {option.patient_id}
                    {option.phone_no ? ` | Phone: ${option.phone_no}` : ""}
                  </Typography>
                </Box>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search by patient name, ID, or phone number..."
              variant="outlined"
              sx={{
                backgroundColor: "#fff",
                borderRadius: "12px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingPatients && (
                      <CircularProgress color="inherit" size={20} />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          noOptionsText="No patients found"
          sx={{ width: "100%" }}
        />

        {selectedPatient && (
          <Box
            sx={{
              mt: 2,
              display: "flex",
              gap: 1.5,
              width: "100%",
            }}
          >
            <Button
              variant="contained"
              onClick={handleOpenCaseSheet}
              startIcon={<DescriptionOutlinedIcon />}
              sx={{
                flex: 1,
                backgroundColor: "#0E3F66",
                color: "#fff",
                textTransform: "none",
                fontSize: { xs: "0.82rem", sm: "0.95rem" },
                fontWeight: 600,
                height: "38px",
                borderRadius: "10px",
                "&:hover": {
                  backgroundColor: "#0E3F66",
                },
              }}
            >
              View Case Sheet
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateAppointment}
              startIcon={<EventAvailableIcon />}
              sx={{
                flex: 1,
                backgroundColor: "#0E3F66",
                color: "#fff",
                textTransform: "none",
                fontSize: { xs: "0.82rem", sm: "0.95rem" },
                fontWeight: 600,
                height: "38px",
                borderRadius: "10px",
                "&:hover": {
                  backgroundColor: "#0E3F66",
                },
              }}
            >
              Create Appointment
            </Button>
            <Button
              variant="contained"
              onClick={handleViewInvoice}
              startIcon={<ReceiptLongIcon />}
              sx={{
                flex: 1,
                backgroundColor: "#0E3F66",
                color: "#fff",
                textTransform: "none",
                fontSize: { xs: "0.82rem", sm: "0.95rem" },
                fontWeight: 600,
                height: "38px",
                borderRadius: "10px",
                "&:hover": {
                  backgroundColor: "#0E3F66",
                },
              }}
            >
              View / Add Invoice
            </Button>
          </Box>
        )}
      </Paper>

      {/* Local Records Content */}
      {selectedPatient && (
        <Paper
          elevation={2}
          sx={{
            borderRadius: "16px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Patient Info Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              pt: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{ color: "#125386", fontWeight: 600 }}
            >
              {displayPatientNameFromParts(selectedPatient, "Patient")} — Local
              Records
            </Typography>
            <Typography sx={{ color: "#666", fontSize: "0.9rem" }}>
              ID: {selectedPatient.patient_id}
            </Typography>
          </Box>

          <Box sx={{ borderBottom: "1px solid #C7C7CC", mt: 1, mx: 1 }} />

          {/* PastVisits (Local data) */}
          <Box sx={{ p: 2 }}>
            <PastVisits
              patientData={selectedPatient}
              fhirArrayData={s3BucketFhir}
              careContextList={careContextList}
              selectedCareContext={selectedContextForRetrieveFhir}
              onSubmitFhirArray={handleFhirArray}
            />
          </Box>
        </Paper>
      )}

      {/* Empty state */}
      {!selectedPatient && (
        <Paper
          elevation={1}
          sx={{
            p: 6,
            borderRadius: "16px",
            textAlign: "center",
            minHeight: "40vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            background: "#fafbfc",
          }}
        >
          <PersonIcon sx={{ fontSize: 80, color: "#ccc", mb: 2 }} />
          <Typography
            variant="h6"
            sx={{ color: "#888", fontWeight: 500, mb: 1 }}
          >
            Search and select a patient to view their historical records
          </Typography>
          <Typography sx={{ color: "#aaa", fontSize: "0.9rem" }}>
            Use the search bar above to find patients by name, ID, or phone
            number
          </Typography>
        </Paper>
      )}

      <CaseSheetModal
        open={isCaseSheetOpen}
        onClose={() => setIsCaseSheetOpen(false)}
        appointment={
          selectedPatient?.patient_id
            ? {
                patient: selectedPatient.patient_id,
                patient_name: selectedPatient.name,
                name: selectedPatient.name,
              }
            : null
        }
        patientData={selectedPatient}
      />
    </Box>
  );
}
